<template>
  <div class="widget">
    <div class="w-full sm:w-1/3 widget-image-holder">
      <img :src="widget.imageUrl" :alt="widget.name" class="widget-image" />
    </div>

    <div class="w-full sm:w-2/3 p-4 lg:p-6">
      <h3 class="widget-name">
        {{ widget.name }}
      </h3>
      <p class="widget-description">
        {{ widget.description }}
      </p>

      <Button class="sm link add-button" :loading="loading" @click="onAddClick">
        Add to Template
      </Button>
    </div>
  </div>
</template>

<script>
import lowerCase from "lodash.lowercase";
import capitalize from "lodash.capitalize";

import { AddSectionToCampaign } from "@/graphql/mutations";
import { Campaign } from "@/graphql/queries";
import EventBus from "@/lib/event-bus.js";

export default {
  name: "Widget",

  props: {
    campaignId: {
      required: true,
      type: String,
    },
    widget: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    integrationName() {
      return capitalize(this.widget.integrationType);
    },
    integrationIconName() {
      return lowerCase(this.widget.integrationType);
    },
  },
  methods: {
    onAddClick: async function() {
      this.loading = true;
      this.$emit("adding-widget", true);

      // Emit the preview loading event to put the Preview component in it's loading state...
      EventBus.$emit("preview-loading", true);

      const addSectionToCampaignInput = {
        input: {
          campaignId: this.campaignId,
          widgetId: this.widget.id,
        },
      };

      try {
        // Send the mutation
        await this.$apollo.mutate({
          mutation: AddSectionToCampaign,
          variables: addSectionToCampaignInput,
          // Update the cache with the result
          // The query will be updated with the optimistic response
          // and then with the real result of the mutation
          update: (store, { data: { addSectionToCampaign } }) => {
            this.updateCampaign(store, addSectionToCampaign);

            EventBus.$emit("refetch-preview");
          },
        });

        this.loading = false;
        this.$emit("adding-widget", false);
      } catch (error) {
        this.loading = false;
        this.$emit("adding-widget", false);

        this.$modal.show("dialog", {
          title: "Oh no 😯",
          text: `Sorry we couldn't add the section. Please try again. If the problem persists please contact our <a href="mailto:support@promo.ai?subject=Promo.ai Error: Add Section&body=%0D%0A%0D%0AError: ${error}">support team</a>.`,
          buttons: [
            {
              title: "Close",
            },
          ],
        });
      }
    },
    updateCampaign(store, response) {
      // Grab the sections from the response or set to an empty array...
      const sections = response.template?.sections ?? [];

      // If we have sections show the success dialog and update the campaign in the cache...
      if (sections.length) {
        this.$modal.show("dialog", {
          title: "Success!",
          text: `The ${this.widget.name} section has been added to your template.`,
          buttons: [
            {
              title: "Edit Layout",
              class: "vue-dialog-button bg-success hover:bg-brand",
              handler: () => {
                this.$modal.hide("dialog");
                this.$modal.hide("widgets");
              },
            },
            {
              title: "Add More",
              class: "vue-dialog-button bg-support-light hover:bg-support",
            },
          ],
        });

        const campaignInput = {
          input: {
            campaignId: this.campaignId,
          },
        };

        // Read the data from our cache for this query.
        const data = store.readQuery({
          query: Campaign,
          variables: campaignInput,
        });

        // Update the cache with the response
        data.campaign.template.sections = response.template?.sections;

        // Write our data back to the cache...
        // This should automatically update the sections as the template is changing...
        store.writeQuery({
          query: Campaign,
          variables: campaignInput,
          data,
        });
      } else {
        this.$modal.show("dialog", {
          title: "Ot oh 😮",
          text: "The widget couldn't be added please try again.",
          buttons: [
            {
              title: "Close",
            },
          ],
        });
      }
    },
  },
};
</script>

<style scoped>
.widget {
  @apply bg-grey-lightest border-grey-lighter border rounded-lg shadow flex flex-wrap items-center overflow-hidden;
}

.widget-image-holder {
  @apply border-b border-grey-lighter bg-white p-2 text-center;

  @screen sm {
    @apply border-r border-b-0;
  }
}

.widget-image {
  @apply m-0;
  max-width: 100px;

  @screen sm {
    max-width: 100%;
  }
}

.widget-name {
  @apply text-xs mb-2;

  @screen md {
    @apply text-lg;
  }
}

.widget-description {
  @apply text-xs text-grey-darker mb-0;

  @screen md {
    @apply text-base;
  }
}

.add-button {
  @apply p-0 text-brand font-lato mt-2 text-xs;

  @screen md {
    @apply text-sm;
  }
}
</style>
